/**
 * Credits and plans — the house's billing page.
 *
 * One balance for the account, drawn by every product; each product on its own
 * plan. Checkout and the Portal are HOSTED by Stripe, so every button here
 * does the same small thing: ask the server for a URL and go to it. No card
 * details ever touch this page.
 *
 * WHICH PLANS GET A BUTTON IS THE SERVER'S ANSWER, not this file's. A card is
 * buyable when `sellables` carries a price for it, so "Boardroom only" needs no
 * condition here and selling a second product later changes nothing on this
 * page. See `billing/checkout.ts`.
 */
import { useCallback, useEffect, useState } from 'react';
import { useEntitlements } from '../contexts/EntitlementContext';
import api from '../services/api';
import { creditsLine, digCost, formatCredits, resetsOn } from '../billing/credits';
import {
  PLAN_ORDER,
  checkoutReturn,
  planOffers,
  priceLabel,
  refreshPlan,
  returnMessage,
  topupOffers,
} from '../billing/checkout';
import { PRODUCT_INFO, PRODUCT_ORDER } from '../shell/products';
import type { CreditLedgerRow, PlanInfo, PlanKey, Product, Sellable } from '../types';

const CALL_LABEL: Record<string, string> = {
  LOOKUP_SERP: 'searches',
  LOOKUP_PAGE: 'page reads',
  LOOKUP_DEMAND: 'demand checks',
  LOOKUP_TRENDS: 'trend checks',
};

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(n);
}

function cap(n: number | null, unit: string): string {
  return n === null ? `Unlimited ${unit}` : `${n} ${unit}${n === 1 ? '' : 's'}`;
}

function PlanCard({
  plan,
  current,
  price,
  onBuy,
  busy,
}: {
  plan: PlanInfo;
  current: boolean;
  price: Sellable | null;
  onBuy: () => void;
  busy: boolean;
}) {
  const amount = priceLabel(price);
  return (
    <div
      className={`rounded-lg border p-4 ${
        current
          ? 'border-blue-400 dark:border-blue-500 bg-blue-50/40 dark:bg-blue-900/10'
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
      }`}
    >
      <div className="flex items-baseline justify-between">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">{plan.label}</h3>
        {current && (
          <span className="text-[10px] font-semibold tracking-widest uppercase text-blue-700 dark:text-blue-300">
            Your plan
          </span>
        )}
      </div>
      {/* THE PRICE COMES FROM STRIPE, read back when an admin saved the row —
          so what is shown here is what the card is charged. */}
      {amount && (
        <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300" data-testid={`price-${plan.key}`}>
          {amount}
        </p>
      )}
      <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
        {formatCredits(plan.monthly_credits)}
        <span className="text-sm font-normal text-gray-500 dark:text-gray-400"> credits / month</span>
      </p>
      <ul className="mt-3 text-sm text-gray-600 dark:text-gray-300 space-y-1">
        <li>{cap(plan.max_rooms, 'open room')}</li>
        <li>{cap(plan.max_personas_per_room, 'persona')} per room</li>
        <li>Runs up to {plan.max_token_budget >= 1_000_000 ? `${plan.max_token_budget / 1_000_000}M` : `${plan.max_token_budget / 1000}k`} tokens</li>
        <li>{plan.byok_allowed ? 'Bring your own model or crawler' : 'House models and crawling'}</li>
      </ul>
      {!current && price && (
        <button
          type="button"
          onClick={onBuy}
          disabled={busy}
          className="mt-4 w-full px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
          data-testid={`buy-${plan.key}`}
        >
          {busy ? 'Opening…' : `Choose ${plan.label}`}
        </button>
      )}
      {/* NOT FOR SALE IS NOT THE SAME AS COMING SOON. Enterprise is a
          conversation, and saying so is more useful than a dead button. */}
      {!current && !price && plan.key === 'enterprise' && (
        <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
          Credits, limits and terms by agreement - talk to us.
        </p>
      )}
    </div>
  );
}

function describe(row: CreditLedgerRow): string {
  if (row.kind === 'CHARGE') {
    const action = (row.action ?? '').toLowerCase().replace(/_/g, ' ');
    const u = row.units ?? {};
    const tokens =
      typeof u.input_tokens === 'number'
        ? ` · ${formatTokens((u.input_tokens as number) + ((u.output_tokens as number) || 0))} tokens`
        : '';
    return `${action}${tokens}${row.byok ? ' (own key)' : ''}`;
  }
  if (row.kind === 'GRANT_PLAN') return 'monthly credits';
  if (row.kind === 'GRANT_TOPUP') return 'top-up';
  if (row.kind === 'EXPIRE') return 'unused credits expired';
  return row.note ? `adjustment · ${row.note}` : 'adjustment';
}

export default function Billing() {
  const {
    entitlements, available, plans, credits, priceBook, planOf, loading, usage,
    sellables, hasStripeCustomer, refresh,
  } = useEntitlements();
  const [ledger, setLedger] = useState<CreditLedgerRow[] | null>(null);
  const [ledgerError, setLedgerError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [payError, setPayError] = useState<string | null>(null);

  useEffect(() => {
    api
      .getCreditLedger(50)
      .then((r) => setLedger(r.rows))
      .catch((e) => setLedgerError(e instanceof Error ? e.message : 'Could not load history'));
  }, [credits.balance]);

  // BACK FROM STRIPE. The row is written by a webhook, which races this
  // redirect — Stripe sends the customer back the instant the payment clears
  // and delivers the event separately, so the first read usually still shows
  // the old plan. There is no entitlement websocket event to wait on, only
  // `credits`, so re-read on a short backoff and then stop: a webhook that has
  // not landed in ten seconds wants looking at, not hiding behind a spinner.
  const landed = checkoutReturn(window.location.search);
  useEffect(() => {
    const timers = refreshPlan(landed).map((ms) => window.setTimeout(() => { void refresh(); }, ms));
    return () => timers.forEach(window.clearTimeout);
  }, [landed, refresh]);

  // Send them where the server says. The URL is Stripe's and is built server
  // side from FRONTEND_URL, so nothing here decides where anyone lands.
  const go = useCallback(async (key: string, make: () => Promise<{ url: string }>) => {
    setBusy(key);
    setPayError(null);
    try {
      const { url } = await make();
      window.location.assign(url);
    } catch (e) {
      setPayError(e instanceof Error ? e.message : 'Could not reach checkout');
      setBusy(null);
    }
  }, []);

  const reset = resetsOn(credits.period_end);
  const pct =
    credits.allowance > 0 ? Math.max(0, Math.min(100, Math.round((credits.plan_credits / credits.allowance) * 100))) : 0;
  const catalog: Product[] = available.length ? available : PRODUCT_ORDER;
  const heldPlans = entitlements.filter((e) => e.active).map((e) => e.plan);
  const bestHeld = (PLAN_ORDER.filter((k) => heldPlans.includes(k as PlanKey)).pop() ?? 'free') as PlanKey;
  // WHICH PRODUCT THE LADDER PRICES. Only one product is sold today, so rather
  // than a picker nobody needs, take the one the server put prices on. When a
  // second arrives this becomes a choice; until then a chooser with one option
  // is furniture.
  const soldProduct: Product | null =
    sellables.find((s) => s.kind === 'SUBSCRIPTION' && s.product)?.product ?? null;
  const offers = soldProduct ? planOffers(plans, sellables, soldProduct, bestHeld) : [];
  const packs = topupOffers(sellables);
  const landedNote = returnMessage(landed);

  return (
    <div className="max-w-4xl mx-auto">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Credits &amp; billing</h1>
        {/* ONE DOOR FOR EVERY SUBSCRIPTION. The Portal lists them all, plus
            invoices and cards, so this is the only management control needed —
            and it is hidden until there is something to manage. */}
        {hasStripeCustomer && (
          <button
            type="button"
            onClick={() => go('portal', () => api.createPortalSession())}
            disabled={busy === 'portal'}
            className="px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60"
            data-testid="manage-billing"
          >
            {busy === 'portal' ? 'Opening…' : 'Manage billing'}
          </button>
        )}
      </header>

      {landedNote && (
        <p
          className="mb-4 rounded border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-3 py-2 text-sm text-blue-900 dark:text-blue-200"
          data-testid="checkout-note"
        >
          {landedNote}
        </p>
      )}
      {payError && (
        <p className="mb-4 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-800 dark:text-red-200">
          {payError}
        </p>
      )}

      {/* THE BALANCE. Two buckets on purpose: the month's allowance expires
          (that is what a subscription buys), a top-up or a comp never does. */}
      <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Credits left</p>
            <p className="text-4xl font-semibold text-gray-900 dark:text-gray-100 tabular-nums" data-testid="credits-balance">
              {loading ? '…' : formatCredits(credits.balance)}
            </p>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              {loading ? '' : creditsLine(credits, priceBook)}
            </p>
          </div>
          <dl className="text-sm text-gray-600 dark:text-gray-300 grid grid-cols-2 gap-x-6 gap-y-1">
            <dt>This month</dt>
            <dd className="tabular-nums text-right">
              {formatCredits(credits.plan_credits)} of {formatCredits(credits.allowance)}
            </dd>
            <dt>Banked</dt>
            <dd className="tabular-nums text-right">{formatCredits(credits.bank_credits)}</dd>
            <dt>Resets</dt>
            <dd className="text-right">{reset ?? '-'}</dd>
          </dl>
        </div>
        <div className="mt-4 h-2 rounded bg-gray-100 dark:bg-gray-700 overflow-hidden" aria-hidden>
          <div className="h-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          Credits are metered on what you use: AI work by the tokens it reads and writes
          {priceBook.llm_rates.standard
            ? ` (${priceBook.llm_rates.standard.input_per_1k} per 1k in, ${priceBook.llm_rates.standard.output_per_1k} per 1k out on the standard tier)`
            : ''}
          , and research by the call - a search is {priceBook.network_rates.LOOKUP_SERP ?? '-'} credits, a page read{' '}
          {priceBook.network_rates.LOOKUP_PAGE ?? '-'}.
          {digCost(priceBook) > 0 && ` A typical dig comes to about ${Math.round(digCost(priceBook))} credits.`}{' '}
          Work on your own key or crawler is charged at {Math.round(priceBook.byok_llm_multiplier * 100)}% of those rates.
        </p>
      </section>

      {/* THE RECEIPT: what this period's credits went on, in real units. */}
      {Object.keys(usage).length > 0 && (
        <section className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">This period</h2>
          <ul className="divide-y divide-gray-200 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            {Object.entries(usage).map(([key, u]) => (
              <li key={key} className="px-4 py-3" data-testid={`usage-${key}`}>
                <div className="flex items-baseline justify-between">
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {PRODUCT_INFO[key as Product]?.name ?? (key === 'house' ? 'Elsewhere' : key)}
                  </p>
                  <p className="tabular-nums text-gray-900 dark:text-gray-100">{formatCredits(u.credits)} credits</p>
                </div>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                  AI {formatCredits(u.llm_credits)} ({formatTokens(u.input_tokens + u.output_tokens)} tokens)
                  {' · '}research {formatCredits(u.network_credits)}
                  {Object.keys(u.calls).length > 0 &&
                    ` (${Object.entries(u.calls)
                      .map(([kind, n]) => `${n} ${CALL_LABEL[kind] ?? kind.toLowerCase()}`)
                      .join(', ')})`}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* PRODUCTS: which you hold, on which plan. */}
      <section className="mt-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Your products</h2>
        <ul className="divide-y divide-gray-200 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          {catalog.map((product) => {
            const info = PRODUCT_INFO[product];
            const row = entitlements.find((e) => e.product === product);
            const held = !!row?.active;
            const plan = plans[planOf(product)];
            return (
              <li key={product} className="flex items-center justify-between gap-4 px-4 py-3">
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{info?.name ?? product}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {held
                      ? row?.monthly_fee_usd != null
                        ? `$${row.monthly_fee_usd}/month · includes ${formatCredits(row.included_credits ?? 0)} credits${
                            row.overage_policy === 'BILL' ? ' · overage billed' : ''
                          }`
                        : `${plan?.label ?? 'Free'} plan`
                      : row
                        ? `Lapsed (${row.status})`
                        : 'Not included'}
                  </p>
                </div>
                <span
                  className={`text-[10px] font-semibold tracking-widest uppercase px-1.5 py-0.5 rounded-sm ${
                    held
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                      : row
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                  }`}
                >
                  {held ? 'Active' : row ? 'Lapsed' : 'Off'}
                </span>
              </li>
            );
          })}
        </ul>
      </section>

      {/* TOP-UPS. Separate from the ladder because they are a different
          promise: a plan's credits expire at the reset, these never do. */}
      {packs.length > 0 && (
        <section className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Buy credits</h2>
          <div className="flex flex-wrap gap-3">
            {packs.map((pack) => (
              <button
                key={pack.stripe_price_id}
                type="button"
                onClick={() => go(pack.stripe_price_id, () => api.createTopupSession(pack.stripe_price_id))}
                disabled={busy === pack.stripe_price_id}
                className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 text-left hover:border-blue-400 dark:hover:border-blue-500 disabled:opacity-60"
                data-testid={`topup-${pack.stripe_price_id}`}
              >
                <span className="block font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
                  {formatCredits(pack.credits ?? 0)} credits
                </span>
                <span className="block text-sm text-gray-500 dark:text-gray-400">
                  {busy === pack.stripe_price_id ? 'Opening…' : (priceLabel(pack) ?? '')}
                </span>
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Bought credits are banked and never expire. They are spent only after the month's allowance runs out.
          </p>
        </section>
      )}

      {/* PLANS, from the server's table; buyable where the server sent a price. */}
      {offers.length > 0 && (
        <section className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Plans</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {offers.map((offer) => (
              <PlanCard
                key={offer.plan.key}
                plan={offer.plan}
                current={offer.current}
                price={offer.price}
                busy={busy === offer.plan.key}
                onBuy={() =>
                  soldProduct &&
                  go(offer.plan.key, () => api.createCheckoutSession(soldProduct, offer.plan.key))
                }
              />
            ))}
          </div>
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            Monthly credits reset each billing period and do not roll over; bought credits never expire.
            Changing plan is prorated by Stripe.
          </p>
        </section>
      )}

      {/* HISTORY */}
      <section className="mt-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">History</h2>
        {ledgerError && <p className="text-sm text-red-600 dark:text-red-400">{ledgerError}</p>}
        {ledger && ledger.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">Nothing yet.</p>
        )}
        {ledger && ledger.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full text-sm">
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                {ledger.map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {row.at ? new Date(row.at).toLocaleString() : ''}
                    </td>
                    <td className="px-4 py-2 text-gray-700 dark:text-gray-200">{describe(row)}</td>
                    <td
                      className={`px-4 py-2 text-right tabular-nums ${
                        row.credits > 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-700 dark:text-gray-200'
                      }`}
                    >
                      {row.credits > 0 ? '+' : ''}
                      {formatCredits(row.credits)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
